/* @moldura padrao */
/**
 * page.tsx (perfis-acessos) — Perfis & Acessos: quem pode o quê, por AÇÃO.
 *
 * **Esta É a tela de Perfis & Acessos** — não há uma "antiga" ao lado. Ela
 * substituiu a matriz de perfil × seção que existia aqui, que desde a Fase 1
 * gravava em `tbl_auth_perfil_secoes` e não decidia mais nada: quem manda no
 * menu e nas rotas é `tbl_auth_perfil_permissoes`. Mesma rota
 * (`/configuracoes/perfis`), mesmo nome no menu, conteúdo novo.
 *
 * PORTADA do `webapp-rm-ops` (lá a tela nasceu como `/acessos` e está sendo
 * renomeada para Perfis & Acessos também) — os dois projetos passam a chamar a
 * mesma coisa pelo mesmo nome. Ver `.claude/plans/ativo/acessos/`.
 *
 * O MOLDE É O DO TEMA: `adminux-company-user-roles` — o `inner-sidebar-wrap`
 * com os grupos expansíveis.
 *
 * SALVAR É EXPLÍCITO, e isto é decisão dele (14/08/2026, no projeto irmão):
 * "marco tudo de caixa e depois como é que eu sei o que estava autorizado?".
 * Gravar a cada clique tornava a exploração irreversível — mexer para ver o que
 * acontece destruía a informação de como estava. Aqui o clique mexe num
 * RASCUNHO: o que difere do salvo aparece destacado e contado, e nada vai ao
 * banco antes de Salvar. Descartar devolve tudo ao estado do servidor.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { CustomAbasPermissoes } from './CustomAbasPermissoes';
import { CustomBuscaAcao } from './CustomBuscaAcao';
import { CustomPainelCalendarios } from './CustomPainelCalendarios';
import { CustomPainelDarAcesso } from './CustomPainelDarAcesso';
import type { EstadoAcao, Modulo } from './CustomAbasPermissoes';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

interface PerfilPacote {
  id: number;
  codigo: string;
  nome: string;
  usuarios: number;
  admin: boolean;
  permissoes: string[];
}

interface DadosPerfis {
  catalogo: Modulo[];
  so_admin: string[];
  perfis: PerfilPacote[];
}

interface UsuarioLinha {
  id: number;
  nome: string;
  perfil_id: number | null;
  perfil_nome: string | null;
  admin: boolean;
  status: 'ativo' | 'inativo';
  excecoes?: { mais: number; menos: number };
}

interface AcaoPessoa {
  // Opcional de propósito: o backend manda, mas a tela deriva da `permissao`
  // quando faltar — ver a nota em `catalogoDaPessoa`.
  codigo?: string;
  permissao: string;
  nome: string;
  descricao: string;
  estado: 'herdado' | 'concedido' | 'bloqueado' | 'sem_acesso';
  no_perfil: boolean;
  tem: boolean;
  editavel: boolean;
  /** A célula de cada loja (migration 126 — o acesso tem âmbito). */
  lojas?: Record<number, { estado: AcaoPessoa['estado']; tem: boolean; no_perfil: boolean }>;
}

interface EstadoPessoa {
  usuario_id: number;
  admin: boolean;
  perfil_nome: string | null;
  lojas: { id: number; codigo: string; nome: string; ativa: boolean }[];
  os_liberadas: { id: number; os_id: number; motivo: string | null; fim: string | null; quem: string | null }[];
  modulos: {
    codigo: string;
    nome: string;
    recursos: { codigo: string; nome: string; acoes: AcaoPessoa[] }[];
  }[];
  excecoes: { mais: number; menos: number };
}

interface QuemPode {
  id: number;
  nome: string;
  via: 'admin' | 'perfil' | 'excecao';
  perfil?: string;
  quem?: string | null;
  motivo?: string | null;
}

type Secao =
  | { tipo: 'perfil'; id: number }
  | { tipo: 'pessoa'; id: number }
  | { tipo: 'de-quem' }
  | { tipo: 'dar' }
  | { tipo: 'quem' }
  | { tipo: 'calendarios' };

/** Como o menu resume o quanto a pessoa foge do próprio perfil. */
function resumoExcecoes(u: UsuarioLinha): string {
  if (u.admin) return 'alcança tudo';
  const partes: string[] = [];
  if (u.excecoes?.mais) partes.push(`${u.excecoes.mais} a mais`);
  if (u.excecoes?.menos) partes.push(`${u.excecoes.menos} a menos`);
  return partes.length ? `${u.perfil_nome ?? 'sem perfil'} · ${partes.join(', ')}` : (u.perfil_nome ?? 'sem perfil');
}

function soExcecoes(u: UsuarioLinha): string {
  if (u.admin) return 'alcança tudo, por código';
  const partes: string[] = [];
  const m = u.excecoes?.mais ?? 0;
  const n = u.excecoes?.menos ?? 0;
  if (m) partes.push(`${m} aç${m === 1 ? 'ão' : 'ões'} a mais que o perfil`);
  if (n) partes.push(`${n} retirada${n === 1 ? '' : 's'}`);
  return partes.join(' · ');
}

/**
 * Barra de "tem coisa por salvar" — igual nos dois painéis.
 *
 * LISTA O QUE VAI ACONTECER, e não só quantas caixinhas mudaram: o efeito de
 * desmarcar depende da ORIGEM do acesso, e o switch sozinho não conta isso.
 * Desmarcar o que vem do perfil retira daquela pessoa; desmarcar o que era
 * extra apenas a devolve ao perfil. Foi exatamente aí que ele se perdeu em
 * 14/08/2026 — tirou uma exceção esperando ver "menos". Escrito em português
 * antes de gravar, a dúvida não chega a existir.
 */
function BarraSalvar({
  mudancas,
  salvando,
  aoSalvar,
  aoDescartar,
}: {
  mudancas: string[];
  salvando: boolean;
  aoSalvar: () => void;
  aoDescartar: () => void;
}) {
  if (mudancas.length === 0) return null;
  return (
    <div className="alert alert-warning py-2 mb-3">
      <div className="d-flex align-items-center justify-content-between">
        <span className="fw-medium">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {mudancas.length}{' '}
          {mudancas.length === 1 ? 'alteração não salva' : 'alterações não salvas'}
        </span>
        <span className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={aoDescartar}
            disabled={salvando}
          >
            Descartar
          </button>
          <button
            type="button"
            className="btn btn-sm btn-theme"
            onClick={aoSalvar}
            disabled={salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </span>
      </div>
      <ul className="mb-0 mt-2 ps-4 small">
        {mudancas.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    </div>
  );
}

export function PerfisAcessosPage() {
  // `?pessoa=<id>` abre direto na pessoa — é como o menu ⋮ da tela de Usuários
  // chega aqui, sem precisar de uma segunda tela dizendo a mesma coisa.
  const [params] = useSearchParams();
  const pessoaDaUrl = Number(params.get('pessoa')) || null;

  const [dados, setDados] = useState<DadosPerfis | null>(null);
  const [usuarios, setUsuarios] = useState<UsuarioLinha[]>([]);
  const [secao, setSecao] = useState<Secao>(
    pessoaDaUrl ? { tipo: 'pessoa', id: pessoaDaUrl } : { tipo: 'quem' },
  );
  /**
   * Os dois grupos nascem RECOLHIDOS (decisão dele, 19/08/2026). Abertos, a
   * lista de gente ocupava a tela inteira antes de alguém pedir por ela — e o
   * menu existe para escolher, não para despejar. Só abre o grupo de quem já
   * chegou apontado pela URL, senão o item selecionado ficaria escondido.
   *
   * Controlado por estado, e não pelas classes `show` do Bootstrap: quem manda
   * no que está aberto é a tela, e assim o estado inicial é uma linha só.
   */
  const [abertos, setAbertos] = useState({ pessoas: !!pessoaDaUrl, perfis: false });
  const alternarGrupo = (qual: 'pessoas' | 'perfis') =>
    setAbertos((a) => ({ ...a, [qual]: !a[qual] }));
  const [erro, setErro] = useState<string | null>(null);

  function carregarUsuarios() {
    api
      .get<UsuarioLinha[]>('/usuarios')
      .then((r) => setUsuarios(r.filter((u) => u.status === 'ativo')))
      .catch(() => setUsuarios([]));
  }

  function carregarPerfis() {
    return api
      .get<DadosPerfis>('/configuracoes/acessos/perfis')
      .then((r) => {
        setDados(r);
        return r;
      })
      .catch((e: unknown) => {
        setErro(e instanceof ApiError ? e.message : 'Não consegui ler os perfis.');
        return null;
      });
  }

  useEffect(() => {
    // Sem pular para o primeiro perfil: a tela abre em "Quem pode o quê", que
    // é o item solto — escolher um perfil dentro de um grupo fechado deixaria a
    // seleção invisível.
    carregarPerfis();
    carregarUsuarios();
  }, []);

  function alternarMenu() {
    document.body.classList.toggle('innermenu-close');
  }

  useEffect(() => () => document.body.classList.remove('innermenu-close'), []);

  if (erro) return <div className="container py-5 text-center text-danger">{erro}</div>;

  return (
    <CustomMolduraPagina
      titulo="Perfis &amp; Acessos"
      descricao="O acesso é por ação, não por tela — a mesma regra desenha o menu e tranca a rota"
    >

      <div className="card adminuiux-card">
        <div className="card-header">
          <div className="row align-items-center">
            <div className="col-auto">
              <button className="btn btn-square btn-link" onClick={alternarMenu}>
                <i className="bi bi-layout-sidebar h5 vm"></i>
              </button>
            </div>
            <div className="col-auto">
              <i className="bi bi-shield-lock h5 avatar avatar-40 bg-theme-1-subtle text-theme-1 rounded"></i>
            </div>
            <div className="col">
              <h6 className="mb-0">Perfis, pessoas e ações</h6>
              <p className="small text-secondary mb-0">
                Perfil é o pacote do grupo; exceção é o ajuste de uma pessoa só
              </p>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="inner-sidebar-wrap">
            <div className="inner-sidebar px-3">
              <ul className="nav nav-pills flex-column mb-3">
                <li className="nav-item dropdown">
                  <a
                    className={`nav-link px-2 dropdown-toggle no-caret ${
                      abertos.pessoas ? 'show' : ''
                    }`}
                    role="button"
                    aria-expanded={abertos.pessoas}
                    onClick={() => alternarGrupo('pessoas')}
                  >
                    <div className="avatar avatar-30 icon rounded-circle">
                      <i className="bi bi-person vm"></i>
                    </div>
                    <div className="col">Pessoas</div>
                    <div className="arrow">
                      <i className="bi bi-chevron-down plus"></i>{' '}
                      <i className="bi bi-chevron-up minus"></i>
                    </div>
                  </a>
                  <ul className={`dropdown-menu ${abertos.pessoas ? 'show' : ''}`}>
                    {usuarios.map((u) => (
                      <li key={u.id}>
                        <a
                          className={`dropdown-item nav-link px-2 ${
                            secao.tipo === 'pessoa' && secao.id === u.id ? 'active' : ''
                          }`}
                          role="button"
                          onClick={() => setSecao({ tipo: 'pessoa', id: u.id })}
                        >
                          <div className="col align-self-center">
                            {u.nome.split(' ')[0]}
                            {/* `text-secondary` no item ATIVO some: o fundo já é
                                a cor do tema e o cinza não contrasta com ele.
                                Ativo herda a cor do link e só perde opacidade. */}
                            <span
                              className={`d-block small ${
                                secao.tipo === 'pessoa' && secao.id === u.id
                                  ? 'opacity-75'
                                  : 'text-secondary'
                              }`}
                            >
                              {resumoExcecoes(u)}
                            </span>
                          </div>
                          <div className="arrow">
                            <i className="bi bi-chevron-right"></i>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>

                <li className="nav-item dropdown">
                  <a
                    className={`nav-link px-2 dropdown-toggle no-caret ${
                      abertos.perfis ? 'show' : ''
                    }`}
                    role="button"
                    aria-expanded={abertos.perfis}
                    onClick={() => alternarGrupo('perfis')}
                  >
                    <div className="avatar avatar-30 icon rounded-circle">
                      <i className="bi bi-clipboard-check vm"></i>
                    </div>
                    <div className="col">Perfis</div>
                    <div className="arrow">
                      <i className="bi bi-chevron-down plus"></i>{' '}
                      <i className="bi bi-chevron-up minus"></i>
                    </div>
                  </a>
                  <ul className={`dropdown-menu ${abertos.perfis ? 'show' : ''}`}>
                    {dados?.perfis.map((p) => (
                      <li key={p.id}>
                        <a
                          className={`dropdown-item nav-link px-2 ${
                            secao.tipo === 'perfil' && secao.id === p.id ? 'active' : ''
                          }`}
                          role="button"
                          onClick={() => setSecao({ tipo: 'perfil', id: p.id })}
                        >
                          <div className="col align-self-center">
                            {p.nome}
                            <span
                              className={`d-block small ${
                                secao.tipo === 'perfil' && secao.id === p.id
                                  ? 'opacity-75'
                                  : 'text-secondary'
                              }`}
                            >
                              {p.usuarios} pessoa{p.usuarios === 1 ? '' : 's'} ·{' '}
                              {p.admin ? 'todas as ações' : `${p.permissoes.length} ações`}
                            </span>
                          </div>
                          <div className="arrow">
                            <i className="bi bi-chevron-right"></i>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>

                <li className="nav-item">
                  <a
                    className={`nav-link px-2 ${secao.tipo === 'de-quem' ? 'active' : ''}`}
                    role="button"
                    onClick={() => setSecao({ tipo: 'de-quem' })}
                  >
                    <div className="avatar avatar-30 icon rounded-circle">
                      <i className="bi bi-person-rolodex"></i>
                    </div>
                    <div className="col">Quem tem qual perfil</div>
                  </a>
                </li>

                <li className="nav-item">
                  <a
                    className={`nav-link px-2 ${secao.tipo === 'dar' ? 'active' : ''}`}
                    role="button"
                    onClick={() => setSecao({ tipo: 'dar' })}
                  >
                    <div className="avatar avatar-30 icon rounded-circle">
                      <i className="bi bi-plus-circle"></i>
                    </div>
                    <div className="col">Dar acesso</div>
                  </a>
                </li>

                <li className="nav-item">
                  <a
                    className={`nav-link px-2 ${secao.tipo === 'calendarios' ? 'active' : ''}`}
                    role="button"
                    onClick={() => setSecao({ tipo: 'calendarios' })}
                  >
                    <div className="avatar avatar-30 icon rounded-circle">
                      <i className="bi bi-calendar2-week"></i>
                    </div>
                    <div className="col">Calendários</div>
                  </a>
                </li>

                <li className="nav-item">
                  <a
                    className={`nav-link px-2 ${secao.tipo === 'quem' ? 'active' : ''}`}
                    role="button"
                    onClick={() => setSecao({ tipo: 'quem' })}
                  >
                    <div className="avatar avatar-30 icon rounded-circle">
                      <i className="bi bi-search"></i>
                    </div>
                    <div className="col">Quem pode o quê</div>
                  </a>
                </li>
              </ul>
            </div>

            <div className="inner-sidebar-content border-start p-3">
              {secao.tipo === 'perfil' && dados && (
                <PainelPerfil
                  key={secao.id}
                  dados={dados}
                  perfilId={secao.id}
                  usuarios={usuarios}
                  aoSalvar={carregarPerfis}
                />
              )}
              {secao.tipo === 'pessoa' && (
                <PainelPessoa
                  key={secao.id}
                  usuario={usuarios.find((u) => u.id === secao.id) ?? null}
                  aoSalvar={carregarUsuarios}
                />
              )}
              {secao.tipo === 'de-quem' && (
                <PainelPerfilDasPessoas
                  perfis={dados?.perfis ?? []}
                  usuarios={usuarios}
                  aoSalvar={() => {
                    carregarUsuarios();
                    carregarPerfis();
                  }}
                />
              )}
              {secao.tipo === 'dar' && (
                <CustomPainelDarAcesso
                  catalogo={dados?.catalogo ?? []}
                  usuarios={usuarios}
                  perfis={dados?.perfis ?? []}
                  soAdmin={dados?.so_admin ?? []}
                  BarraSalvar={BarraSalvar}
                  aoSalvar={() => {
                    carregarPerfis();
                    carregarUsuarios();
                  }}
                />
              )}
              {secao.tipo === 'quem' && (
                <PainelQuemPode catalogo={dados?.catalogo ?? []} usuarios={usuarios} />
              )}
              {secao.tipo === 'calendarios' && (
                <CustomPainelCalendarios
                  usuarios={usuarios}
                  perfis={dados?.perfis ?? []}
                  BarraSalvar={BarraSalvar}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </CustomMolduraPagina>
  );
}

/* ─── Perfil ─────────────────────────────────────────────────────────────── */

function PainelPerfil({
  dados,
  perfilId,
  usuarios,
  aoSalvar,
}: {
  dados: DadosPerfis;
  perfilId: number;
  usuarios: UsuarioLinha[];
  aoSalvar: () => void;
}) {
  const perfil = dados.perfis.find((p) => p.id === perfilId);
  const salvas = new Set(perfil?.permissoes ?? []);
  const [rascunho, setRascunho] = useState<Set<string>>(new Set(perfil?.permissoes ?? []));
  const [salvando, setSalvando] = useState(false);
  const exclusivas = new Set(dados.so_admin);

  if (!perfil) return null;

  const daCasa = usuarios.filter((u) => u.perfil_id === perfil.id);
  const alteradas = [...new Set([...salvas, ...rascunho])].filter(
    (p) => salvas.has(p) !== rascunho.has(p),
  );

  const nomeDaAcao = (permissao: string) => {
    const [mod, rec, acao] = permissao.split('.');
    const a = dados.catalogo
      .find((m) => m.codigo === mod)
      ?.recursos.find((r) => r.codigo === rec)
      ?.acoes.find((x) => x.codigo === acao);
    return a?.nome ?? permissao;
  };

  const quantos = perfil.usuarios === 1 ? 'a 1 pessoa' : `às ${perfil.usuarios} pessoas`;
  const mudancas = alteradas.map((p) =>
    rascunho.has(p)
      ? `${nomeDaAcao(p)}: passa a valer ${quantos} deste perfil`
      : `${nomeDaAcao(p)}: deixa de valer ${quantos} deste perfil`,
  );

  const estadoDe = (permissao: string): EstadoAcao => ({
    ligada: rascunho.has(permissao),
    editavel: !perfil.admin && !exclusivas.has(permissao),
    alterada: salvas.has(permissao) !== rascunho.has(permissao),
    aviso: exclusivas.has(permissao) ? (
      <span className="text-secondary">
        <i className="bi bi-lock-fill me-1"></i>
        Só administrador, por código — não se concede pela tela.
      </span>
    ) : undefined,
  });

  async function salvar() {
    setSalvando(true);
    try {
      await api.put(`/configuracoes/acessos/perfis/${perfil!.id}`, { permissoes: [...rascunho] });
      toast.success(`Perfil ${perfil!.nome} atualizado.`);
      aoSalvar();
    } catch (e: unknown) {
      toast.error(e instanceof ApiError ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <h5 className="mb-2">
        {perfil.admin && <i className="bi bi-shield-check text-theme-1 me-2"></i>}
        {perfil.nome}
      </h5>
      <p className="text-secondary">
        {perfil.admin ? (
          <>
            Alcança o sistema inteiro <strong>por código</strong> — não há o que
            marcar aqui. É o que impede alguém de se trancar para fora numa
            caixinha errada.
          </>
        ) : (
          <>
            Dá <strong>{salvas.size} ações</strong> a{' '}
            <strong>
              {perfil.usuarios} pessoa{perfil.usuarios === 1 ? '' : 's'}
            </strong>
            . Mexer aqui muda todas de uma vez — e não apaga a exceção de ninguém.
          </>
        )}
      </p>

      <BarraSalvar
        mudancas={mudancas}
        salvando={salvando}
        aoSalvar={salvar}
        aoDescartar={() => setRascunho(new Set(perfil.permissoes))}
      />

      <CustomAbasPermissoes
        catalogo={dados.catalogo}
        estadoDe={estadoDe}
        aoAlternar={(permissao) =>
          setRascunho((atual) => {
            const proximo = new Set(atual);
            if (proximo.has(permissao)) proximo.delete(permissao);
            else proximo.add(permissao);
            return proximo;
          })
        }
        aoAlternarModulo={
          perfil.admin
            ? undefined
            : (m, ligar) => {
                const doModulo = m.recursos
                  .flatMap((r) => r.acoes.map((a) => `${m.codigo}.${r.codigo}.${a.codigo}`))
                  .filter((p) => !exclusivas.has(p));
                setRascunho((atual) => {
                  const proximo = new Set(atual);
                  doModulo.forEach((p) => (ligar ? proximo.add(p) : proximo.delete(p)));
                  return proximo;
                });
              }
        }
        abasExtras={[
          {
            codigo: '_pessoas',
            nome: 'Pessoas neste perfil',
            icone: 'bi-people-fill',
            conteudo: (
              <div className="mb-2">
                {daCasa.length === 0 ? (
                  <p className="text-secondary">Ninguém usa este perfil hoje.</p>
                ) : (
                  <ul className="list-group list-group-flush bg-none">
                    {daCasa.map((u) => (
                      <li className="list-group-item text-secondary" key={u.id}>
                        <span className="fw-medium text-body">{u.nome}</span>
                        {(u.excecoes?.mais || u.excecoes?.menos) && (
                          <span className="small ms-2">
                            {!!u.excecoes?.mais && (
                              <span className="text-success me-2">
                                +{u.excecoes.mais} além do perfil
                              </span>
                            )}
                            {!!u.excecoes?.menos && (
                              <span className="text-danger">
                                −{u.excecoes.menos} retirada(s)
                              </span>
                            )}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-secondary mt-2 mb-0">
                  Mudar o pacote muda o acesso de todas elas de uma vez.
                </p>
              </div>
            ),
          },
        ]}
      />
    </>
  );
}

/* ─── Pessoa ─────────────────────────────────────────────────────────────── */

function PainelPessoa({
  usuario,
  aoSalvar,
}: {
  usuario: UsuarioLinha | null;
  aoSalvar: () => void;
}) {
  const [estado, setEstado] = useState<EstadoPessoa | null>(null);
  // Desde a migration 126 o acesso tem ÂMBITO: cada loja é uma coluna, com o
  // seu conjunto. `salvas`/`rascunho` são loja → permissões.
  const [salvas, setSalvas] = useState<Record<number, Set<string>>>({});
  const [rascunho, setRascunho] = useState<Record<number, Set<string>>>({});
  const [lojasSalvas, setLojasSalvas] = useState<Set<number>>(new Set());
  const [lojasRascunho, setLojasRascunho] = useState<Set<number>>(new Set());
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);

  function absorver(r: EstadoPessoa) {
    const porLoja: Record<number, Set<string>> = {};
    for (const loja of r.lojas) {
      porLoja[loja.id] = new Set(
        r.modulos.flatMap((m) =>
          m.recursos.flatMap((rec) =>
            rec.acoes.filter((a) => a.lojas?.[loja.id]?.tem).map((a) => a.permissao),
          ),
        ),
      );
    }
    const ativas = new Set(r.lojas.filter((l) => l.ativa).map((l) => l.id));
    setEstado(r);
    setSalvas(porLoja);
    setRascunho(Object.fromEntries(Object.entries(porLoja).map(([k, v]) => [k, new Set(v)])));
    setLojasSalvas(ativas);
    setLojasRascunho(new Set(ativas));
  }

  // Sem setState no corpo do efeito: a página remonta este painel a cada pessoa
  // (`key={id}`), então o estado inicial já é o do começo — a regra de hooks que
  // derrubou /caixa/cofre em 10/08/2026.
  useEffect(() => {
    if (!usuario) return;
    api
      .get<EstadoPessoa>(`/usuarios/${usuario.id}/permissoes`)
      .then(absorver)
      .catch((e: unknown) =>
        toast.error(e instanceof ApiError ? e.message : 'Não consegui ler os acessos.'),
      );
  }, [usuario]);

  if (!usuario) return null;

  const lojas = estado?.lojas ?? [];
  const acoes = new Map<string, AcaoPessoa>(
    (estado?.modulos ?? []).flatMap((m) =>
      m.recursos.flatMap((r) => r.acoes.map((a) => [a.permissao, a] as [string, AcaoPessoa])),
    ),
  );

  // O componente monta a permissão pelos três segmentos; a resposta da pessoa
  // traz `permissao` pronta. Derivar o `codigo` que faltar da própria string
  // evita o modo de falha SILENCIOSO que apareceu em 14/08/2026: sem ele, a
  // permissão virava `modulo.recurso.undefined`, nada casava, e a tela mostrava
  // TUDO com cadeado — dizendo que a pessoa não tinha nada e nada podia ser
  // mexido. Errar assim é pior do que quebrar.
  const catalogoDaPessoa: Modulo[] = (estado?.modulos ?? []).map((m) => ({
    codigo: m.codigo,
    nome: m.nome,
    recursos: m.recursos.map((r) => ({
      codigo: r.codigo,
      nome: r.nome,
      acoes: r.acoes.map((a) => ({
        codigo: a.codigo ?? a.permissao.split('.')[2],
        nome: a.nome,
        descricao: a.descricao,
      })),
    })),
  }));

  const perfilNome = estado?.perfil_nome ?? 'perfil';
  const nomeLoja = (id: number) => lojas.find((l) => l.id === id)?.codigo ?? String(id);
  const naLoja = (permissao: string, loja: number) =>
    lojasRascunho.has(loja) && !!rascunho[loja]?.has(permissao);
  const salvaNaLoja = (permissao: string, loja: number) =>
    lojasSalvas.has(loja) && !!salvas[loja]?.has(permissao);
  const doPerfilNaLoja = (permissao: string, loja: number) =>
    !!acoes.get(permissao)?.lojas?.[loja]?.no_perfil;

  const aviso = (a: AcaoPessoa | undefined) =>
    a?.editavel ? undefined : (
      <span className="text-secondary">
        <i className="bi bi-lock-fill me-1"></i>
        Só administrador, por código — não se concede pela tela.
      </span>
    );

  const estadoNaColuna = (permissao: string, loja: number): EstadoAcao => {
    const a = acoes.get(permissao);
    const ligada = naLoja(permissao, loja);
    const noPerfil = doPerfilNaLoja(permissao, loja);
    let excecao: EstadoAcao['excecao'];
    if (lojasRascunho.has(loja) && ligada && !noPerfil)
      excecao = {
        icone: 'bi-plus-circle-fill',
        classe: 'text-success',
        titulo: `Extra desta pessoa em ${nomeLoja(loja)} — o perfil ${perfilNome} não dá isto.`,
      };
    else if (lojasRascunho.has(loja) && !ligada && noPerfil)
      excecao = {
        icone: 'bi-dash-circle-fill',
        classe: 'text-danger',
        titulo: `Retirado só dela em ${nomeLoja(loja)} — o perfil ${perfilNome} dá isto.`,
      };
    return {
      ligada,
      editavel: !!a?.editavel,
      alterada: salvaNaLoja(permissao, loja) !== ligada,
      excecao,
      aviso: aviso(a),
    };
  };

  // A linha inteira: a união das colunas — é o que as abas contam.
  const estadoDe = (permissao: string): EstadoAcao => {
    const a = acoes.get(permissao);
    return {
      ligada: lojas.some((l) => naLoja(permissao, l.id)),
      editavel: !!a?.editavel,
      alterada: lojas.some((l) => salvaNaLoja(permissao, l.id) !== naLoja(permissao, l.id)),
      aviso: aviso(a),
    };
  };

  const ativas = lojas.filter((l) => lojasRascunho.has(l.id));
  const mais = ativas.reduce(
    (n, l) =>
      n + [...(rascunho[l.id] ?? [])].filter((p) => acoes.get(p) && !doPerfilNaLoja(p, l.id)).length,
    0,
  );
  const menos = ativas.reduce(
    (n, l) =>
      n +
      [...acoes.values()].filter((a) => doPerfilNaLoja(a.permissao, l.id) && !naLoja(a.permissao, l.id))
        .length,
    0,
  );
  const temExcecao = mais > 0 || menos > 0;

  // Cada mudança dita em português, com o EFEITO — que depende da origem e da loja.
  const mudancas: string[] = [];
  for (const l of lojas) {
    const entrou = lojasRascunho.has(l.id) && !lojasSalvas.has(l.id);
    const saiu = !lojasRascunho.has(l.id) && lojasSalvas.has(l.id);
    if (entrou) mudancas.push(`${l.codigo}: passa a estar nesta loja, com o perfil ${perfilNome}`);
    if (saiu) {
      mudancas.push(`${l.codigo}: sai desta loja — deixa de ver e fazer qualquer coisa nela`);
      continue;
    }
    if (!lojasRascunho.has(l.id)) continue;
    for (const a of acoes.values()) {
      const antes = salvaNaLoja(a.permissao, l.id);
      const agora = naLoja(a.permissao, l.id);
      if (antes === agora || (entrou && agora === doPerfilNaLoja(a.permissao, l.id))) continue;
      const noPerfil = doPerfilNaLoja(a.permissao, l.id);
      if (agora && noPerfil) mudancas.push(`${l.codigo} · ${a.nome}: volta a ter, como o perfil dá`);
      else if (agora) mudancas.push(`${l.codigo} · ${a.nome}: passa a ter, como extra só dela`);
      else if (noPerfil) mudancas.push(`${l.codigo} · ${a.nome}: deixa de ter, retirado só dela`);
      else mudancas.push(`${l.codigo} · ${a.nome}: deixa de ser extra e ela volta ao perfil`);
    }
  }

  // O pacote do perfil, loja a loja — é para onde "Voltar ao perfil" leva. Entra
  // como RASCUNHO, e não grava direto: a lista de mudanças mostra o que vai
  // sumir antes de sumir, que é a diferença entre um atalho e uma cilada.
  function voltarAoPerfil() {
    setRascunho(
      Object.fromEntries(
        lojas.map((l) => [
          l.id,
          new Set([...acoes.values()].filter((a) => doPerfilNaLoja(a.permissao, l.id)).map((a) => a.permissao)),
        ]),
      ),
    );
  }

  function alternarLoja(loja: number) {
    setLojasRascunho((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(loja)) {
        proximo.delete(loja);
      } else {
        proximo.add(loja);
        // Entrar na loja = receber o perfil nela. As exceções que ela tinha lá
        // voltam junto, se a loja já tinha sido dela (estão em `salvas`).
        if (!lojasSalvas.has(loja))
          setRascunho((r) => ({
            ...r,
            [loja]: new Set(
              [...acoes.values()].filter((a) => doPerfilNaLoja(a.permissao, loja)).map((a) => a.permissao),
            ),
          }));
      }
      return proximo;
    });
  }

  async function salvar() {
    setSalvando(true);
    try {
      const mudouLojas =
        lojasRascunho.size !== lojasSalvas.size || [...lojasRascunho].some((l) => !lojasSalvas.has(l));
      if (mudouLojas)
        await api.put(`/usuarios/${usuario!.id}/lojas`, { lojas: [...lojasRascunho] });
      const r = await api.put<EstadoPessoa>(`/usuarios/${usuario!.id}/permissoes`, {
        por_loja: Object.fromEntries([...lojasRascunho].map((l) => [l, [...(rascunho[l] ?? [])]])),
        motivo: motivo.trim() || null,
      });
      absorver(r);
      setMotivo('');
      toast.success(`Acessos de ${usuario!.nome.split(' ')[0]} atualizados.`);
      aoSalvar();
    } catch (e: unknown) {
      toast.error(e instanceof ApiError ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  function descartar() {
    setRascunho(Object.fromEntries(Object.entries(salvas).map(([k, v]) => [k, new Set(v)])));
    setLojasRascunho(new Set(lojasSalvas));
  }

  return (
    <>
      {/* O perfil ao lado do nome era um badge escuro, que competia com o nome
          e desalinhava a linha. Aqui ele é o que de fato é: um complemento —
          mesma linha de base, peso normal, cor secundária. */}
      <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-1">
        <h5 className="mb-0">
          {usuario.nome}
          {estado?.perfil_nome && (
            <span className="fs-6 fw-normal text-secondary ms-2">
              {estado.perfil_nome}
            </span>
          )}
        </h5>
        {estado && !estado.admin && (temExcecao || mudancas.length > 0) && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={voltarAoPerfil}
            title={`Apaga as exceções desta pessoa: em cada loja dela, exatamente o que o perfil ${perfilNome} dá`}
          >
            <i className="bi bi-arrow-counterclockwise me-1"></i>
            Voltar ao perfil
          </button>
        )}
      </div>

      {!estado ? (
        <div className="d-flex align-items-center gap-2 py-5 text-secondary">
          <span className="spinner-border spinner-border-sm" /> Carregando…
        </div>
      ) : estado.admin ? (
        <div className="text-center py-5 text-secondary">
          <i className="bi bi-shield-lock fs-1 text-theme-1"></i>
          <p className="mt-3 mb-0">
            Administrador alcança tudo, nas duas lojas, por código — não há o que marcar aqui.
          </p>
        </div>
      ) : (
        <>
          {/* Uma linha por sinal, em vez de um parágrafo corrido: são três
              coisas distintas, e lidas em bloco elas se confundiam justamente
              onde a confusão custa caro. */}
          <ul className="list-unstyled text-secondary small lh-lg mb-3">
            <li>
              <i className="bi bi-shop opacity-50 me-1"></i>uma coluna por loja —
              marcada no topo, a pessoa recebe nela o perfil <strong>{perfilNome}</strong>
            </li>
            <li>
              <i className="bi bi-plus-circle-fill text-success me-1"></i>
              acrescentado só a ela{mais > 0 ? ` — hoje são ${mais}` : ''}
            </li>
            <li>
              <i className="bi bi-dash-circle-fill text-danger me-1"></i>
              retirado só dela{menos > 0 ? ` — hoje são ${menos}` : ''}
            </li>
            {!temExcecao && <li className="fst-italic">Hoje ela é igual ao perfil nas lojas dela.</li>}
          </ul>

          <BarraSalvar
            mudancas={mudancas}
            salvando={salvando}
            aoSalvar={salvar}
            aoDescartar={descartar}
          />

          {mudancas.length > 0 && (
            <div className="input-group input-group-sm rounded mb-3">
              <span className="input-group-text text-theme-1">
                <i className="bi bi-chat-left-text"></i>
              </span>
              <input
                type="text"
                className="form-control"
                maxLength={255}
                placeholder="Motivo (opcional) — fica no registro de quem deu"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </div>
          )}

          <CustomAbasPermissoes
            catalogo={catalogoDaPessoa}
            estadoDe={estadoDe}
            aoAlternar={() => undefined}
            colunas={lojas.map((l) => ({
              id: l.id,
              rotulo: l.codigo,
              ativa: lojasRascunho.has(l.id),
              alterada: lojasRascunho.has(l.id) !== lojasSalvas.has(l.id),
              aoAlternar: () => alternarLoja(l.id),
            }))}
            estadoNaColuna={estadoNaColuna}
            aoAlternarNaColuna={(permissao, loja) =>
              setRascunho((atual) => {
                const conjunto = new Set(atual[loja] ?? []);
                if (conjunto.has(permissao)) conjunto.delete(permissao);
                else conjunto.add(permissao);
                return { ...atual, [loja]: conjunto };
              })
            }
          />

          {estado.os_liberadas.length > 0 && (
            <div className="mt-4">
              <h6 className="small text-uppercase text-secondary mb-2">
                OS dadas uma a uma, fora das lojas dela
              </h6>
              <ul className="list-group list-group-flush">
                {estado.os_liberadas.map((o) => (
                  <li key={o.id} className="list-group-item px-0 small">
                    <Link to={`/ordens-servico/${o.os_id}`}>OS #{o.os_id}</Link>
                    <span className="text-secondary">
                      {' · '}
                      {o.motivo}
                      {' · '}
                      {o.fim ? `até ${formatarData(o.fim)}` : 'sem prazo'}
                      {o.quem && ` · deu: ${o.quem}`}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="small text-secondary mb-0">
                Dá-se e tira-se na própria OS, no bloco "Quem mais vê esta OS".
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * O eixo 1 do acesso, que até 19/08/2026 não existia nesta tela: o perfil de
 * cada pessoa só se trocava em Usuários ou na ficha do funcionário — telas de
 * CADASTRO. Quem administrava acesso via "+3 além do perfil" sem poder dizer
 * qual era o perfil.
 *
 * É UMA LISTA, e não um painel por pessoa, por decisão dele: *"faz uma listazinha
 * igual onde a gente tem hoje quem pode o quê"*. A pergunta é sobre o conjunto
 * — quem é o quê aqui dentro — e cabe num olhar. Enfiar isso no painel da
 * pessoa misturaria o pacote (perfil) com o ajuste (exceção), que é justamente o
 * que a tela separa.
 *
 * Salvar é EXPLÍCITO, como no resto da tela: trocar perfil muda mais acesso de
 * uma vez do que qualquer exceção.
 */
function PainelPerfilDasPessoas({
  perfis,
  usuarios,
  aoSalvar,
}: {
  perfis: PerfilPacote[];
  usuarios: UsuarioLinha[];
  aoSalvar: () => void;
}) {
  // rascunho[idDaPessoa] = perfil escolhido (null = sem perfil)
  const [rascunho, setRascunho] = useState<Record<number, number | null>>({});
  const [salvando, setSalvando] = useState(false);

  function perfilDe(u: UsuarioLinha): number | null {
    return u.id in rascunho ? rascunho[u.id] : u.perfil_id;
  }

  const alterados = usuarios.filter((u) => u.id in rascunho && rascunho[u.id] !== u.perfil_id);
  const mudancas = alterados.map((u) => {
    const alvo = rascunho[u.id];
    const nome = alvo === null ? 'sem perfil' : (perfis.find((p) => p.id === alvo)?.nome ?? '?');
    const antes = u.perfil_nome ?? 'sem perfil';
    return `${u.nome}: ${antes} → ${nome}`;
  });

  async function salvar() {
    setSalvando(true);
    try {
      // Uma chamada por pessoa — são poucas, e assim uma recusa (o próprio
      // administrador tentando se rebaixar) não derruba as outras trocas.
      for (const u of alterados) {
        await api.put(`/configuracoes/acessos/pessoas/${u.id}/perfil`, { perfil_id: rascunho[u.id] });
      }
      toast.success(
        alterados.length === 1
          ? `Perfil de ${alterados[0].nome.split(' ')[0]} atualizado.`
          : `${alterados.length} perfis atualizados.`,
      );
      setRascunho({});
      aoSalvar();
    } catch (e: unknown) {
      toast.error(e instanceof ApiError ? e.message : 'Não consegui salvar.');
      // O RASCUNHO CAI JUNTO — sem isto, a linha recusada continuava exibindo o
      // perfil que o servidor negou, e a tela passava a mentir: o `select` dizia
      // "Funcionária" enquanto a pessoa seguia Administrador no banco. As trocas
      // que já gravaram antes do erro não se perdem (foram uma chamada cada); o
      // recarregar traz a verdade de todas.
      setRascunho({});
      aoSalvar();
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <h5 className="mb-2">Quem tem qual perfil</h5>
      <p className="text-secondary">
        O perfil é o <strong>pacote base</strong> da pessoa. O ajuste individual —
        o que ela tem a mais ou a menos que o perfil — fica em <em>Pessoas</em>.
      </p>

      <BarraSalvar
        mudancas={mudancas}
        salvando={salvando}
        aoSalvar={salvar}
        aoDescartar={() => setRascunho({})}
      />

      {usuarios.length === 0 ? (
        <p className="text-secondary">Nenhuma pessoa ativa.</p>
      ) : (
        <ul className="list-group list-group-flush bg-none">
          {usuarios.map((u) => {
            const mudou = u.id in rascunho && rascunho[u.id] !== u.perfil_id;
            return (
              <li className="list-group-item" key={u.id}>
                <div className="row gx-3 align-items-center">
                  <div className="col-12 col-sm">
                    <span className="fw-medium">{u.nome}</span>
                    {/* Só o que o `select` ao lado NÃO diz. O `resumoExcecoes`
                        do menu começa pelo nome do perfil, que aqui já está na
                        direita — repetir seria dizer "Funcionária · Funcionária". */}
                    {soExcecoes(u) && (
                      <span className="d-block small text-secondary">{soExcecoes(u)}</span>
                    )}
                  </div>
                  <div className="col-12 col-sm-auto mt-2 mt-sm-0">
                    <select
                      className={`form-control form-control-sm ${mudou ? 'border-theme-1' : ''}`}
                      value={perfilDe(u) ?? ''}
                      aria-label={`Perfil de ${u.nome}`}
                      onChange={(e) =>
                        setRascunho((atual) => ({
                          ...atual,
                          [u.id]: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    >
                      <option value="">— sem perfil</option>
                      {perfis.map((p) => (
                        <option value={p.id} key={p.id}>
                          {p.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/* ─── Quem pode o quê ────────────────────────────────────────────────────── */

/* ─── Quem pode o quê — só consulta ──────────────────────────────────────── */
//
// Ela responde; quem AGE é "Dar acesso", ao lado. Até 19/08/2026 as duas coisas
// moravam aqui, e a parte de conceder ficava escondida no fim, aparecendo só
// depois de dois cliques certos — ninguém achava.

function PainelQuemPode({
  catalogo,
  usuarios,
}: {
  catalogo: Modulo[];
  usuarios: UsuarioLinha[];
}) {
  const [permissao, setPermissao] = useState<string | null>(null);
  const [pessoas, setPessoas] = useState<QuemPode[] | null>(null);

  // Escolher e limpar passam por aqui, e não pelo efeito: zerar `pessoas` dentro
  // do `useEffect` é `setState` em efeito — uma renderização a mais para dizer o
  // que o clique já sabia.
  function escolher(nova: string | null) {
    setPermissao(nova);
    setPessoas(null);
  }

  useEffect(() => {
    if (!permissao) return;
    api
      .get<{ pessoas: QuemPode[] }>(`/configuracoes/acessos/quem-pode/${permissao}`)
      .then((r) => setPessoas(r.pessoas))
      .catch((e: unknown) => {
        setPessoas([]);
        toast.error(e instanceof ApiError ? e.message : 'Não consegui consultar.');
      });
  }, [permissao]);

  return (
    <>
      <h5 className="mb-2">Quem pode o quê</h5>
      <p className="text-secondary">
        A pergunta ao contrário: procure a ação e veja quem chega nela — e por
        qual caminho. Para <strong>dar</strong> uma ação a alguém, use{' '}
        <em>Dar acesso</em>, aqui ao lado.
      </p>

      <CustomBuscaAcao
        catalogo={catalogo}
        escolhida={permissao}
        aoEscolher={escolher}
        vazio={
          <>
            <p className="text-secondary small">
              Quem existe no sistema hoje. Digite acima para procurar por ação.
            </p>
            <ul className="list-group list-group-flush bg-none">
              {usuarios.map((u) => (
                <li className="list-group-item" key={u.id}>
                  <div className="row gx-3 align-items-center">
                    <div className="col fw-medium">{u.nome}</div>
                    <div className="col-auto text-end">
                      {u.admin ? (
                        <span className="badge text-bg-dark">administrador (tudo)</span>
                      ) : (
                        <span className="badge text-bg-secondary">
                          {u.perfil_nome ?? 'sem perfil'}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        }
      />

      {!permissao ? null : pessoas === null ? (
        <div className="text-secondary small">Carregando…</div>
      ) : pessoas.length === 0 ? (
        <p className="text-secondary">Ninguém alcança esta ação.</p>
      ) : (
        <ul className="list-group list-group-flush bg-none">
          {pessoas.map((p) => (
            <li className="list-group-item text-secondary" key={p.id}>
              <div className="row gx-3 align-items-center">
                <div className="col fw-medium text-body">{p.nome}</div>
                <div className="col-auto text-end">
                  {p.via === 'admin' && (
                    <span className="badge text-bg-dark">administrador (tudo)</span>
                  )}
                  {p.via === 'perfil' && (
                    <span className="badge text-bg-secondary">perfil {p.perfil}</span>
                  )}
                  {p.via === 'excecao' && (
                    <>
                      <span className="badge text-bg-success">extra desta pessoa</span>
                      <span className="d-block small">
                        {p.quem ? `dada por ${p.quem}` : 'sem registro de quem deu'}
                        {p.motivo ? ` — ${p.motivo}` : ''}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {permissao && (
        <p className="text-secondary mt-3 mb-0">
          Esta lista vale para a porta, não só para o menu: quem não alcança a
          ação leva recusa também ao digitar a URL.
        </p>
      )}
    </>
  );
}
